import { Canvas } from '@react-three/fiber'
import { TrackballControls } from '@react-three/drei'
import { useState, useEffect } from 'react'
import { Viewcube } from './ViewCube.js'
import { button, useControls } from 'leva'
import { Cloud } from './cloud.js'
import SentenceContext from './context/SentenceContext.js'
import DeviceContext from './context/DeviceContext.js'
import { Perf } from 'r3f-perf'
import languages from './languages.js'
import AppShell from './AppShell.js'
import LoginScreen from './login/LoginScreen.js'
import { DialogProvider } from './context/DialogContext.js'
import UserContext from './context/UserContext.js'
import { LOGIN_MUTATION, REGISTER_MUTATION } from './query/gqlQueries.js'
import { useMutation } from '@apollo/client'

const languageOptions = Object.values(languages)
const shuffledLanguageOptions = Object.values(languages).sort(() => Math.random() - 0.5)
const owners = ['Shared', 'My', 'Tatoeba']
// const ownersExtended = ['My', 'Tatoeba', 'Others', 'Our']
// const labels = ['Sentences', 'Words', 'Conversations', 'Monologue', 'Thoughts', 'Projects', 'Self Improvement', 'Tasks', 'Inspiration']
const labels = ['Sentences']

export default function App() {
  // Active sentence and current translation
  const [activeSentenceObj, setActiveSentenceObj] = useState(null)
  const [activeTranslationObj, setActiveTranslationObj] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const [username, setUsername] = useState('')
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [registerUsername, setRegisterUsername] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [targetLanguage, setTargetLanguage] = useState('Portuguese')
  const [sourceLanguage, setSourceLanguage] = useState('English')
  const [owner, setOwner] = useState('My')
  const [label, setLabel] = useState('Sentences')

  const [login] = useMutation(LOGIN_MUTATION)
  const [register] = useMutation(REGISTER_MUTATION)
  // New accounts are students unless the administrator says otherwise; only
  // owner/team accounts see the work tools.
  const [registerRole, setRegisterRole] = useState('student')

  const handleLogin = async () => {
    try {
      setLoading(true)
      const response = await login({ variables: { username: loginUsername, password: loginPassword } })
      const token = response.data.login.token
      localStorage.setItem('token', token) // Store the token in local storage
      setIsLoggedIn(true)
      setUsername(loginUsername)
      setLoading(false)
      setFeedbackMessage('Login successful!')
    } catch (error) {
      setLoading(true)
      setFeedbackMessage('Login failed: ' + error.message)
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    try {
      await register({ variables: { username: registerUsername, password: registerPassword, role: registerRole } })
      setFeedbackMessage(`Registration successful (${registerRole}). Please login.`)
    } catch (error) {
      setFeedbackMessage('Registration failed: ' + error.message)
    }
  }

  const [queryData, setQueryData] = useState([
    {
      sourceLanguage: 'eng',
      source: 'What do you want to learn today',
      sourceId: null,
      sourceType: null,
      targetLanguage: 'por',
      target: 'O que é que você quer aprender hoje?',
      targetId: null,
      targetType: null
    }
  ])

  // Change data into source and target sentence
  const handleData = (data) => {
    if (data.length === 0) {
      setQueryData([
        {
          sourceLanguage: 'eng',
          source: 'No matching sentences found',
          sourceId: null,
          sourceType: null,
          targetLanguage: 'por',
          target: 'Não foram encontradas frases correspondentes',
          targetId: null,
          targetType: null
        }
      ])
    } else {
      setQueryData(data)
    }
  }

  useEffect(() => {
    const checkIfMobile = () => {
      return (
        typeof window.orientation !== 'undefined' ||
        navigator.userAgent.indexOf('IEMobile') !== -1 ||
        window.matchMedia('only screen and (max-width: 767px)').matches ||
        navigator.userAgent.match(/iPad/i) !== null
      )
    }

    setIsMobile(checkIfMobile())
  }, [])

  let count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible

  if (process.env.REACT_APP_DEV === 'True') {
    // Assign values using useControls when in dev mode
    ;({ count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible } = useControls({
      count: { value: 4, min: 1, max: 16, step: 1 },
      radius: { value: 20, min: 10, max: 50, step: 1 },
      fontSize: { value: 1.2, min: 0.5, max: 5, step: 0.1 },
      primaryColor: { value: '#FFDE7D' },
      secondaryColor: { value: '#F2BE22' },
      tertiaryColor: { value: '#F29727' },
      hoverColor: { value: '#00ADB5' },
      voiceSpeed: { value: 0.75, min: 0.25, max: 1.5, step: 0.05 },
      removeFocus: button(() => resetActiveSentence()),
      perfVisible: false,
      tickerVisible: false
    }))
  } else {
    ;({ count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible } = {
      count: 4,
      radius: 20,
      fontSize: 1.2,
      primaryColor: '#FFDE7D',
      secondaryColor: '#F2BE22',
      tertiaryColor: '#F29727',
      hoverColor: '#00ADB5',
      voiceSpeed: 0.75,
      removeFocus: () => resetActiveSentence(),
      perfVisible: false,
      tickerVisible: false
    })
  }

  // Add the resetActiveSentence function
  const resetActiveSentence = () => {
    setActiveSentenceObj(null)
    setActiveTranslationObj(null)
  }

  if (!isLoggedIn) {
    return (
      <LoginScreen
        username={loginUsername}
        setUsername={setLoginUsername}
        password={loginPassword}
        setPassword={setLoginPassword}
        onLogin={handleLogin}
        loading={loading}
        message={feedbackMessage}
        register={
          process.env.REACT_APP_REGISTER === 'True'
            ? {
                username: registerUsername,
                setUsername: setRegisterUsername,
                password: registerPassword,
                setPassword: setRegisterPassword,
                role: registerRole,
                setRole: setRegisterRole,
                onRegister: handleRegister
              }
            : null
        }
      />
    )
  }

  return (
    <>
      <Canvas dpr={[1, 2]} camera={{ position: [0, 0, 35], fov: 90 }}>
        {perfVisible && <Perf position="top-left" />}
        <fog attach="fog" args={['#202025', 0, 80]} />
        <DeviceContext.Provider value={{ isMobile }}>
          <SentenceContext.Provider value={{ activeSentenceObj, setActiveSentenceObj, activeTranslationObj, setActiveTranslationObj }}>
            <Cloud
              count={count}
              radius={radius}
              fontSize={fontSize}
              sentenceList={queryData}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              tertiaryColor={tertiaryColor}
              hoverColor={hoverColor}
              targetLanguage={targetLanguage}
              sourceLanguage={sourceLanguage}
              label={label}
              owner={owner}
              voiceSpeed={voiceSpeed}
            />
          </SentenceContext.Provider>
        </DeviceContext.Provider>
        <TrackballControls />

        <Viewcube username={username} />
      </Canvas>

      <UserContext.Provider value={{ username, isLoggedIn }}>
        <DeviceContext.Provider value={{ isMobile }}>
          <SentenceContext.Provider value={{ activeSentenceObj, setActiveSentenceObj, activeTranslationObj, setActiveTranslationObj }}>
            <DialogProvider>
              <AppShell
                sourceLanguage={sourceLanguage}
                setSourceLanguage={setSourceLanguage}
                targetLanguage={targetLanguage}
                setTargetLanguage={setTargetLanguage}
                sentenceList={queryData}
                onData={handleData}
                label={label}
                setLabel={setLabel}
                owner={owner}
                setOwner={setOwner}
                labels={labels}
                owners={owners}
                languageOptions={languageOptions}
                tickerVisible={tickerVisible}
                voiceSpeed={voiceSpeed}
              />
            </DialogProvider>
          </SentenceContext.Provider>
        </DeviceContext.Provider>
      </UserContext.Provider>
    </>
  )
}
